import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../services/Interceptor";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../services/AthleteService";
import {
  getOffers,
  updateOffer,
  createOffer,
  deleteOffer,
  getAccolades,
  updateAthletePassword,
  updateAccolade,
  createAccolade,
  deleteAccolade,
} from "../services/AdminService";
import { toast } from "react-toastify";
import { positions, states } from "../utils/PositionStateData";
import { useForm } from "react-hook-form";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import moment from "moment";
import AthleteProfileTab from "./athlete-profile-modal/AthleteProfileTab";
import AthleteOffersTab from "./athlete-profile-modal/AthleteOffersTab";
import AthleteAccoladesTab from "./athlete-profile-modal/AthleteAccoladesTab";
import AthletePasswordTab from "./athlete-profile-modal/AthletePasswordTab";
import GrantSubscriptionDialog from "./athlete-profile-modal/GrantSubscriptionDialog";
import { GrantFreeSubscription } from "../services/PaymentService";
import { divisions } from "../utils/PositionStateData";

export const getApiErrorMessage = (error, fallbackMessage) => {
  if (typeof error === "string") return error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.message && typeof error.message === "string") return error.message;
  if (error?.error && typeof error.error === "string") return error.error;
  return fallbackMessage;
};

export default function AthleteProfileModal({ athlete, onClose, onSave }) {
  const authUser = useAuthUser();
  const isAdmin = authUser?.user?.role === "admin";
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const apiInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const date = moment();

  const [offers, setOffers] = useState([]);
  const [accolades, setAccolades] = useState([]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordSubmitError, setPasswordSubmitError] = useState("");
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
  const [isGrantSubscriptionOpen, setIsGrantSubscriptionOpen] = useState(false);
  const [isGrantingSubscription, setIsGrantingSubscription] = useState(false);

  // Offer form
  const {
    register: offerRegister,
    handleSubmit: handleOfferSubmit,
    reset: resetOffer,
  } = useForm();

  // Accolade form with react-hook-form
  const {
    register: accoladeRegister,
    handleSubmit: handleAccoladeSubmit,
    reset: resetAccolade,
    resetField,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      accoladeType: "ALL_STATE",
      accoladeYear: date.year(),
    },
  });

  const {
    register: passwordRegister,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    getValues: getPasswordValues,
    clearErrors: clearPasswordErrors,
    formState: { errors: passwordErrors, isValid: isPasswordFormValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  // Grant subscription form
  const {
    register: grantSubscriptionRegister,
    handleSubmit: handleGrantSubscriptionSubmit,
    watch: watchGrantSubscription,
    setValue: setGrantSubscriptionValue,
    reset: resetGrantSubscription,
    formState: { errors: grantSubscriptionErrors },
  } = useForm({
    defaultValues: { durationOption: "7", customDays: "" },
  });

  const grantDurationOption = watchGrantSubscription("durationOption");
  const DURATION_OPTION_DAYS = { 7: 7, 30: 30, 90: 90, 365: 365 };

  // Watch the accoladeType field
  const selectedType = watch("accoladeType");
  useEffect(() => {
    resetField("accoladeDescription");
  }, [resetField, selectedType]);

  const [deleteOfferOpen, setDeleteOfferOpen] = useState(false);
  const [deleteAccoladeOpen, setDeleteAccoladeOpen] = useState(false);
  const [offerIdSelected, setOfferIdSelected] = useState(undefined);
  const [accoladeIdSelected, setAccoladeIdSelected] = useState(undefined);

  let [isOfferOpen, setIsOfferOpen] = useState(false);
  let [isAccoladeOpen, setIsAccoladeOpen] = useState(false);

  const [isOfferEdit, setIsOfferEdit] = useState(false);
  const [isAccoladeEdit, setIsAccoladeEdit] = useState(false);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedAccolade, setSelectedAccolade] = useState(null);

  const [profileData, setProfileData] = useState({
    username: "",
    email: "",
    gpa: "",
    highSchool: "",
    expectedYearOfGraduation: "",
    city: "",
    state: "",
    primaryPosition: "",
    secondaryPosition: "",
    universityOfInterest: "",
    academicDegreeOfInterest: "",
    instagramHandle: "",
    twitterHandle: "",
    athleteEmail: "",
    parentEmail: "",
    athletePhone: "",
    parentPhone: "",
    transcriptFilePath: "",
    footballClub: "",
    hasCurrentSubscription: false,
    subscriptionEndDate: null,
  });
  const [isSubscriptionStatusLoading, setIsSubscriptionStatusLoading] =
    useState(true);

  // Accolade options
  const ALL_STATE_OPTIONS = [
    "All State 1st Team",
    "All State 2nd Team",
    "All State Honorable Mention",
  ];

  const ALL_CONFERENCE_OPTIONS = [
    "All Conference/Region 1st Team",
    "All Conference/Region 2nd Team",
    "All Conference/Region Honorable Mention",
  ];

  const CLASSIFICATIONS = ["1A", "2A", "3A", "4A", "5A", "6A"];

  const openCreateOffer = () => {
    setIsOfferEdit(false);
    setSelectedOffer(null);
    resetOffer();
    setIsOfferOpen(true);
  };

  const openEditOffer = (offer) => {
    setIsOfferEdit(true);
    setSelectedOffer(offer);
    resetOffer({
      schoolName: offer.schoolName,
      division: offer.division,
      offerYear: offer.offerYear,
    });
    setIsOfferOpen(true);
  };

  const openCreateAccolade = () => {
    setIsAccoladeEdit(false);
    setSelectedAccolade(null);
    resetAccolade({
      accoladeType: "ALL_STATE",
      accoladeYear: date.year(),
    });
    setIsAccoladeOpen(true);
  };

  const openEditAccolade = (accolade) => {
    setIsAccoladeEdit(true);
    setSelectedAccolade(accolade);

    // Map API response to form fields
    const formData = {
      accoladeType: accolade.accoladeLink || "ALL_STATE",
      accoladeDescription: accolade.accoladeDescription || "",
      accoladeClassification: accolade.accoladeClassification || "",
      accoladeYear: accolade.accoladeYear || date.year(),
    };

    resetAccolade(formData);
    setIsAccoladeOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const showApiErrorToast = (error, fallbackMessage) => {
    toast.error(getApiErrorMessage(error, fallbackMessage));
  };

  const formatSubscriptionDate = (value) =>
    value ? moment(value).format("MM/DD/YYYY") : "";

  const openGrantSubscription = () => {
    resetGrantSubscription({ durationOption: "7", customDays: "" });
    setIsGrantSubscriptionOpen(true);
  };

  const handleGrantSubscriptionSubmission = async (data) => {
    const days =
      data.durationOption === "custom"
        ? Number(data.customDays)
        : DURATION_OPTION_DAYS[data.durationOption];

    try {
      setIsGrantingSubscription(true);
      const result = await GrantFreeSubscription(
        apiInstance,
        athlete.userId,
        days,
      );
      const subscriptionEndDate =
        result?.subscriptionEndDate ?? moment().add(days, "days").toISOString();

      setProfileData((prev) => ({
        ...prev,
        hasCurrentSubscription: true,
        subscriptionEndDate,
      }));
      onSave?.({ hasCurrentSubscription: true, subscriptionEndDate });
      toast.success(
        `Free subscription granted! New expiration date: ${formatSubscriptionDate(
          subscriptionEndDate,
        )}.`,
      );
      resetGrantSubscription({ durationOption: "7", customDays: "" });
      setIsGrantSubscriptionOpen(false);
    } catch (error) {
      showApiErrorToast(error, "Failed to grant free subscription.");
    } finally {
      setIsGrantingSubscription(false);
    }
  };

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAthleteProfile(apiInstance, athlete.userId);
        if (response) {
          setProfileData((prev) => ({
            ...prev,
            ...response,
            username: response.username || response.email || prev.username,
            email: response.email || response.username || prev.email,
          }));
        }
      } catch (error) {
        showApiErrorToast(error, "Failed to load athlete profile.");
      } finally {
        setIsSubscriptionStatusLoading(false);
      }
    };

    if (athlete && athlete.userId) {
      setIsSubscriptionStatusLoading(true);
      getProfile();
    } else {
      setIsSubscriptionStatusLoading(false);
    }
  }, [athlete, apiInstance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => {
      if (name === "username") {
        return {
          ...prev,
          username: value,
          email: value,
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const currentUsernameOrEmail = profileData.username || profileData.email;
      const updatedProfileData = isAdmin
        ? profileData
        : {
            ...profileData,
            username: currentUsernameOrEmail,
            email: currentUsernameOrEmail,
          };
      const updatedProfile = await updateAthleteProfile(
        apiInstance,
        athlete.userId,
        updatedProfileData,
      );
      setProfileData((prev) => ({
        ...prev,
        ...updatedProfile,
        username:
          updatedProfile?.username || updatedProfile?.email || prev.username,
        email: updatedProfile?.email || updatedProfile?.username || prev.email,
      }));
      onSave?.(updatedProfile);
      toast.success("Athlete profile updated successfully!");
      handleClose();
    } catch (error) {
      toast.error(
        "Error updating athlete profile: " +
          (error.response?.data?.message || "Unknown error"),
      );
    }
  };

  const openDeleteOffer = (offerId) => {
    setOfferIdSelected(offerId);
    setDeleteOfferOpen(true);
  };

  const openDeleteAccoladeModal = (accoladeId) => {
    setAccoladeIdSelected(accoladeId);
    setDeleteAccoladeOpen(true);
  };

  // Offer form submission
  const handleOfferSubmission = async (data) => {
    try {
      if (isOfferEdit && selectedOffer) {
        await updateOffer(apiInstance, data, athlete.userId, selectedOffer.id);
        toast.success("Offer updated successfully!");
      } else {
        await createOffer(apiInstance, data, athlete.userId);
        toast.success("Offer created successfully!");
      }

      resetOffer();
      setIsOfferOpen(false);
      await getAthleteOffers();
    } catch (err) {
      showApiErrorToast(err, "Failed to save offer.");
    }
  };

  // Accolade form submission
  const handleAccoladeSubmission = async (data) => {
    try {
      // Map form data to API payload
      const payload = {
        accoladeDescription: data.accoladeDescription,
        accoladeClassification: data.accoladeClassification,
        accoladeYear: Number(data.accoladeYear),
        accoladeLink: data.accoladeType || null,
      };

      if (isAccoladeEdit && selectedAccolade) {
        await updateAccolade(
          apiInstance,
          payload,
          athlete.userId,
          selectedAccolade.id,
        );
        toast.success("Accolade updated successfully!");
      } else {
        await createAccolade(apiInstance, payload, athlete.userId);
        toast.success("Accolade created successfully!");
      }

      resetAccolade({
        accoladeType: "ALL_STATE",
        accoladeYear: date.year(),
      });
      setIsAccoladeOpen(false);
      await getAthleteAccolades();
    } catch (err) {
      showApiErrorToast(err, "Failed to save accolade.");
    }
  };

  const getAthleteOffers = async () => {
    try {
      setLoading(true);
      const data = await getOffers(apiInstance, athlete.userId);
      setOffers(Array.isArray(data) ? data : []);
    } catch (error) {
      setOffers([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAthleteAccolades = async () => {
    try {
      setLoading(true);
      const data = await getAccolades(apiInstance, athlete.userId);
      setAccolades(Array.isArray(data) ? data : []);
    } catch (error) {
      setAccolades([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteOfferItem = async () => {
    try {
      await deleteOffer(apiInstance, offerIdSelected, athlete.userId);
      await getAthleteOffers();
      setDeleteOfferOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete offer", {
        position: "top-right",
      });
    }
  };

  const deleteAccoladeItem = async () => {
    try {
      await deleteAccolade(apiInstance, accoladeIdSelected, athlete.userId);
      await getAthleteAccolades();
      setDeleteAccoladeOpen(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete accolade",
        { position: "top-right" },
      );
    }
  };

  const handleOfferDeleteConfirm = async () => {
    try {
      await deleteOfferItem();
    } catch (err) {
      showApiErrorToast(err, "Failed to delete offer.");
    }
  };

  const handleAccoladeDeleteConfirm = async () => {
    try {
      await deleteAccoladeItem();
    } catch (err) {
      showApiErrorToast(err, "Failed to delete accolade.");
    }
  };

  const openPasswordTab = () => {
    setActiveTab("password");
    setPasswordSubmitError("");
    clearPasswordErrors();
    resetPasswordForm();
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };

  const handlePasswordUpdate = async (data) => {
    setPasswordSubmitError("");

    try {
      setIsPasswordUpdating(true);
      await updateAthletePassword(
        apiInstance,
        athlete.userId,
        data.newPassword,
      );
      toast.success("Password updated successfully.");
      resetPasswordForm();
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        "Failed to update athlete password.",
      );
      setPasswordSubmitError(message);
    } finally {
      setIsPasswordUpdating(false);
    }
  };

  const tabs = [
    {
      id: "profile",
      label: "Athlete Profile",
      onClick: () => setActiveTab("profile"),
    },
    {
      id: "offers",
      label: "Offers",
      onClick: async () => {
        setActiveTab("offers");
        try {
          await getAthleteOffers();
        } catch (error) {
          showApiErrorToast(error, "Failed to load offers.");
        }
      },
    },
    {
      id: "accolades",
      label: "Accolades",
      onClick: async () => {
        setActiveTab("accolades");
        try {
          await getAthleteAccolades();
        } catch (error) {
          showApiErrorToast(error, "Failed to load accolades.");
        }
      },
    },
    {
      id: "password",
      label: "Password",
      onClick: openPasswordTab,
    },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case "profile":
        return (
          <AthleteProfileTab
            isAdmin={isAdmin}
            profileData={profileData}
            handleChange={handleChange}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            positions={positions}
            states={states}
          />
        );
      case "offers":
        return (
          <AthleteOffersTab
            athleteId={athlete.userId}
            offers={offers}
            openCreateOffer={openCreateOffer}
            openEditOffer={openEditOffer}
            openDeleteOffer={openDeleteOffer}
            isOfferOpen={isOfferOpen}
            setIsOfferOpen={setIsOfferOpen}
            isOfferEdit={isOfferEdit}
            handleOfferSubmit={handleOfferSubmit}
            handleOfferSubmission={handleOfferSubmission}
            offerRegister={offerRegister}
            divisions={divisions}
            date={date}
            loading={loading}
            resetOffer={resetOffer}
            setSelectedOffer={setSelectedOffer}
            setIsOfferEdit={setIsOfferEdit}
            deleteOfferOpen={deleteOfferOpen}
            setDeleteOfferOpen={setDeleteOfferOpen}
            handleOfferDeleteConfirm={handleOfferDeleteConfirm}
            onAcceptanceChange={(status) =>
              onSave?.({ offerAcceptanceStatus: status })
            }
          />
        );
      case "accolades":
        return (
          <AthleteAccoladesTab
            accolades={accolades}
            openCreateAccolade={openCreateAccolade}
            openEditAccolade={openEditAccolade}
            openDeleteAccoladeModal={openDeleteAccoladeModal}
            isAccoladeOpen={isAccoladeOpen}
            setIsAccoladeOpen={setIsAccoladeOpen}
            isAccoladeEdit={isAccoladeEdit}
            handleAccoladeSubmit={handleAccoladeSubmit}
            handleAccoladeSubmission={handleAccoladeSubmission}
            selectedType={selectedType}
            setValue={setValue}
            errors={errors}
            accoladeRegister={accoladeRegister}
            allStateOptions={ALL_STATE_OPTIONS}
            allConferenceOptions={ALL_CONFERENCE_OPTIONS}
            classifications={CLASSIFICATIONS}
            date={date}
            loading={loading}
            resetAccolade={resetAccolade}
            deleteAccoladeOpen={deleteAccoladeOpen}
            setDeleteAccoladeOpen={setDeleteAccoladeOpen}
            handleAccoladeDeleteConfirm={handleAccoladeDeleteConfirm}
          />
        );
      case "password":
        return (
          <AthletePasswordTab
            handlePasswordSubmit={handlePasswordSubmit}
            handlePasswordUpdate={handlePasswordUpdate}
            passwordRegister={passwordRegister}
            passwordSubmitError={passwordSubmitError}
            setPasswordSubmitError={setPasswordSubmitError}
            passwordErrors={passwordErrors}
            getPasswordValues={getPasswordValues}
            isPasswordFormValid={isPasswordFormValid}
            isPasswordUpdating={isPasswordUpdating}
            showNewPassword={showNewPassword}
            setShowNewPassword={setShowNewPassword}
            showConfirmNewPassword={showConfirmNewPassword}
            setShowConfirmNewPassword={setShowConfirmNewPassword}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 relative">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            {/* Header */}
            <h2 className="text-lg font-semibold mb-2 uppercase">
              {athlete?.name} {athlete?.lastname}
            </h2>

            {/* Subscription banner (admin only) */}
            {isAdmin && (
              <div
                className={`flex items-center rounded-md border px-4 py-3 mb-4 ${
                  isSubscriptionStatusLoading
                    ? "justify-center border-gray-200 bg-gray-50"
                    : profileData.hasCurrentSubscription
                      ? "justify-center border-green-200 bg-green-50"
                      : "justify-between border-gray-200 bg-gray-50"
                }`}
              >
                {isSubscriptionStatusLoading ? (
                  <span className="text-sm font-medium text-gray-400">
                    Checking subscription status...
                  </span>
                ) : profileData.hasCurrentSubscription ? (
                  <span className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-green-600"
                    />
                    Subscribed
                    {profileData.subscriptionEndDate && (
                      <span className="font-normal text-gray-600">
                        until{" "}
                        {formatSubscriptionDate(
                          profileData.subscriptionEndDate,
                        )}
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    <span className="text-sm font-medium text-gray-600">
                      Not subscribed
                    </span>
                    <button
                      type="button"
                      onClick={openGrantSubscription}
                      className="text-sm text-white bg-blue-600 hover:bg-blue-800 rounded-md px-3 py-1.5 uppercase"
                    >
                      Grant free subscription
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b mb-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={tab.onClick}
                  className={`px-4 py-2 font-medium ${
                    activeTab === tab.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {renderActiveTab()}
          </div>
        </div>
      )}
      {isAdmin && (
        <GrantSubscriptionDialog
          open={isGrantSubscriptionOpen}
          onClose={() => setIsGrantSubscriptionOpen(false)}
          handleGrantSubscriptionSubmit={handleGrantSubscriptionSubmit}
          handleGrantSubscriptionSubmission={handleGrantSubscriptionSubmission}
          grantSubscriptionRegister={grantSubscriptionRegister}
          setGrantSubscriptionValue={setGrantSubscriptionValue}
          grantDurationOption={grantDurationOption}
          grantSubscriptionErrors={grantSubscriptionErrors}
          isGrantingSubscription={isGrantingSubscription}
          resetGrantSubscription={resetGrantSubscription}
        />
      )}
    </>
  );
}
